import React from 'react'
import styles from './OrderForm.module.css'
import { OrderItem, OrderUpdateError } from '../../types/Order'
import Banner from '../Banner'
import { getFormattedPrice } from '../../helpers/formatPrice'
import { connect } from 'react-redux'
import { Dispatch } from '../../models'
import logError from '../../utils/logging'
import CountStepper from '../CountStepper'
import Variants from '../Variants'
import loader from '../../icons/loading.gif'
import { Product, Variant } from '../../types/Product'
import TextArea from '../TextArea'

interface Props {
  orderItem: OrderItem
  originalTotalPrice: number
  isCardPayment: boolean
  error?: OrderUpdateError
  customerComment?: string
  noBottomSpacing?: boolean
  getCateringProduct: (productId: string) => Promise<Product | undefined>
  onAmountChange?: (count: number | string, id: string) => void
  onSelectVariant: (orderItemId: string, productPrice: number, taxCode: number, variant: Variant) => void
  onCustomerCommentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void  
}

function UpdateProduct ({ orderItem, originalTotalPrice, isCardPayment, error, customerComment, noBottomSpacing, onSelectVariant, getCateringProduct, onAmountChange, onCustomerCommentChange }: Props) {
  const [productToEdit, setProductToEdit] = React.useState<Product>({} as Product)
  const [count, setCount] = React.useState<number>(orderItem?.amount)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [comment, setComment] = React.useState<string>('')

  const onChangeCount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0
    setCount(value)
    onAmountChange && onAmountChange(value, orderItem.id)
  }

  const onMinus = () => {
    if(count > 0) {
      setCount(prev => prev - 1)
      onAmountChange && onAmountChange(count - 1, orderItem.id)
    }
  }

  const onPlus = () => {
    setCount(prev => prev  + 1)
    onAmountChange && onAmountChange(count + 1, orderItem.id)
  }

  React.useEffect(() => {
    if (customerComment && customerComment !== comment) {
      setComment(customerComment)
    }
  }, [customerComment])

  React.useEffect(() => { 
    let isMounted = true
    const fetchProduct = async () => {
      try {
        const res = await getCateringProduct(orderItem.productId)
        if (isMounted && res) {
          setProductToEdit(res)
          setLoading(false)
        }
      } catch (error) {
        if (isMounted) {
          setLoading(false)
          logError(error as Error)
        }
      }
    }
    fetchProduct()
    return () => {
      isMounted = false
    }
  }, [orderItem.id])

  const handleSelectedVariant = (variant: Variant) => { 
    onSelectVariant(orderItem.id, productToEdit.price, productToEdit.taxCode, variant )
  }


  return (
    <>
      {loading ? (
        <div className={styles.loaderContainer}><img src={loader} className={styles.loaderSpinner} alt="loading" /></div>
      ) : (
        <> 
          <div className={`${styles.bannerContainer} ${noBottomSpacing ? styles.noBottomSpacing : ''}`}>
            {(error?.isError || isCardPayment) && (
              <Banner
                theme={error?.isError ? 'warning' : 'info'}>
                <span className={styles.bannerText}>
                  {isCardPayment ? (
                    <>
                      <span className={styles.bannerTextBold}>Kortbetalning:</span> {error?.isError ? (
                        <>
                          {error?.errorText} <span className={styles.bannerTextBold}>Ändra betalsätt till &quot;betala i butik&quot; för att öka beloppet.</span>
                        </>
                      ) : (
                        <>Det går inte att genomföra ändringar som ökar det reserverade beloppet på {getFormattedPrice(originalTotalPrice)}. Ändra betalsätt till &quot;betala i butik&quot; för att öka beloppet.</>
                      )}
                    </>
                  ) : (
                    <span className={styles.bannerTextBold}>Ändringarna kunde inte sparas</span>
                  )}
                </span>
              </Banner>
            )}
          </div>
          <div className={`${styles.productEditSection} ${noBottomSpacing ? styles.noBottomSpacing : ''}`}>
            <div>
              <h4 className={styles.heading}>{productToEdit?.name}</h4>
              {orderItem.variantId && productToEdit?.variantCategories && productToEdit?.variantCategories.length > 0 && (
                <Variants product={productToEdit} variantId={orderItem.variantId} predefined={true} onSelectVariant={(variant: Variant) => handleSelectedVariant(variant)} />
              )} 
            </div>
            {productToEdit.allowComment && (
              <>
                <span className={styles.fieldLabel}>Meddelande</span>
                <div className={styles.productCommentSection}> 
                  <TextArea 
                    value={customerComment as string} 
                    placeholder='Skriv en kommentar (valfritt)' 
                    setComment={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      setComment(e.target.value)
                      onCustomerCommentChange(e)
                    }}
                  />
                </div>
              </>
            )}
            
            <div className={styles.productSummarySection}>
              <div className={styles.productQuantityRow}>
                <div>
                  <span className={styles.fieldLabel}>Antal</span>
                </div>        
                <div className={styles.productStepper}>
                  <span className={styles.productPriceLabel}>{getFormattedPrice(orderItem.priceGross)}/st</span>
                  <CountStepper
                    count={count} 
                    minValue={0}
                    error={error?.isError}
                    onMinus={onMinus} 
                    onPlus={onPlus} 
                    onBlur={(e: React.ChangeEvent<HTMLInputElement>) => onChangeCount(e)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeCount(e)}  />
                </div>
              </div>
              <div className={styles.productTotalRow}>
                <div className={styles.productTotalColumn}>
                  <span className={styles.productTotalPrice}>Totalt</span>
                  <span className={styles.productArticleCount}>{count === 1 ? `${count} artikel ` : `${count} artiklar`}</span>
                </div>
                <div className={styles.productTotalColumn}>
                  <span className={styles.productTotalPrice}>
                    {getFormattedPrice(Math.round(orderItem.priceGross * count * 100) * 0.01)}
                  </span>
                  <span className={styles.productVatAmount}>Moms: {getFormattedPrice(orderItem.vatSubTotal * count)}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

function mapDispatch (dispatch: Dispatch) {
  return {
    getCateringProduct: dispatch.order.getCateringProduct,
  }
}

export default connect(null, mapDispatch)(UpdateProduct)